import { useTranslation } from 'react-i18next'
import { Post, PostMediaItem } from '../../../../model/domain/news'
import { translate } from '../../../../../I18n/i18n'
import { User } from '../../../../model/domain/user'
import { Fragment } from 'react'
import NewsCardProfilePicture from '../NewsCardProfilePicture/NewsCardProfilePicture'
import { GetLocale, Role } from '@cuatroochenta/co-admin-react-library'

interface NewsCardProps {
    post: Post
    user: User
    media: { [key: number]: { [key: number]: string | null } | null }
    showReactions: { [key: number]: boolean }
    onEdit: (post: Post) => void
    onDelete: (postId: Post['id']) => void
    onViewImages: (index: number, mediaItems: PostMediaItem[]) => void
    onViewVideo: (mediaItemId: PostMediaItem['id']) => void
    onLike: (post: Post, likeTypeId: number) => void
    onEnterReactions: () => void
    onLeaveReactions: () => void
}

function NewsCard({
    post,
    user,
    media,
    showReactions,
    onEdit,
    onDelete,
    onViewImages,
    onViewVideo,
    onLike,
    onEnterReactions,
    onLeaveReactions,
}: NewsCardProps) {
    const { t } = useTranslation()

    const color = post.publisher.profile_color
    const mediaLength = post.media?.data?.length ?? 0
    const isOverflow = mediaLength > 4
    const left = mediaLength - 4

    const formatDate = (dateString: string) => {
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        }

        const date = new Date(dateString)
        const formattedDate = date.toLocaleString(GetLocale(), options)
        return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)
    }

    return (
        <div className="card-news shadow mb-3" key={post.id}>
            <div className="card-body">
                <div className="card-container-header">
                    <div
                        className="card-image-profile"
                        style={{ backgroundColor: color }}
                    >
                        <NewsCardProfilePicture
                            fullName={post.publisher.full_name}
                            profilePicture={post.publisher.photo}
                        />
                    </div>
                    <div className="card-container-text-header">
                        <p className="card-text-bold">
                            {post.publisher.full_name}
                        </p>
                        <p className="card-text-date text-muted">
                            <small>{formatDate(post.created_at)}</small>
                        </p>
                    </div>
                    {(user.data.roles.includes(Role.SUPERADMIN) || user.data.id === post.publisher.id) && (
                        <div className="card-actions">
                            <button
                                className="action-btn edit-btn"
                                onClick={() => onEdit(post)}
                            >
                                <i className="fas fa-edit"></i>{' '}
                                {/* Icono de editar */}
                            </button>
                            <button
                                className="action-btn delete-btn"
                                onClick={() => onDelete(post.id)}
                            >
                                <i className="fas fa-trash-alt"></i>{' '}
                                {/* Icono de borrar */}
                            </button>
                        </div>
                    )}
                </div>

                <p className="card-text">
                    {post.content.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </p>

                {post.media && post.media.data && post.media.data.length > 0 ? (
                    <div
                        className={`media-container ${
                            post.media.data.length === 1
                                ? 'single-item'
                                : post.media.data.length <= 2
                                ? 'single-row'
                                : ''
                        }`}
                    >
                        {post.media.data.slice(0, 4).map((mediaItem, index) => {
                            const mediaUrl =
                                media[post.id]?.[mediaItem.id] || null
                            const uniqueKey = `${post.id}-${mediaItem.id}`
                            const isVideo = post.media?.media_type === 'video'

                            return (
                                <div className="media-item" key={uniqueKey}>
                                    {mediaUrl ? (
                                        <div
                                            className="media-image-container"
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                onViewImages(
                                                    index,
                                                    post.media?.data ?? []
                                                )
                                            }}
                                        >
                                            <img
                                                className="media-image"
                                                src={mediaUrl}
                                                alt=""
                                                style={{
                                                    background:
                                                        "center / cover url('/icons/media_fallback.png')",
                                                }}
                                            />
                                            {isVideo && (
                                                <>
                                                    <div
                                                        className="overlay-play-icon"
                                                        onClick={(event) => {
                                                            event.stopPropagation()
                                                            onViewVideo(
                                                                mediaItem.id
                                                            )
                                                        }}
                                                    ></div>
                                                    <div className="play-icon">
                                                        <svg
                                                            width="50"
                                                            height="50"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M8 5v14l11-7-11-7z"
                                                                fill="white"
                                                            />
                                                        </svg>
                                                    </div>
                                                </>
                                            )}
                                            {/* Si es la última imagen y hay más de 4 imágenes, muestra el overlay encima de la cuarta imagen */}
                                            {index === 3 && isOverflow && (
                                                <div className="overlay-last-image">
                                                    +{left}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div>
                                            {translate(
                                                t,
                                                'news.TR_LOADING_LABEL'
                                            )}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <></>
                )}

                <div
                    className="post-actions"
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                    }}
                >
                    {post.total_likes.map((likeType) => {
                        let iconPath

                        if (likeType.likeTypeId === 1) {
                            iconPath = '/icons/me_gusta.svg'
                        } else if (likeType.likeTypeId === 2) {
                            iconPath = '/icons/me_encanta.svg'
                        } else if (likeType.likeTypeId === 3) {
                            iconPath = '/icons/me_divierte.svg'
                        }

                        return (
                            <div
                                className="likes-section"
                                style={{ position: 'relative' }}
                                key={likeType.likeTypeId}
                            >
                                <img
                                    src={iconPath}
                                    alt={likeType.likeTypeName}
                                    style={{ width: '20px', height: '20px' }}
                                />

                                {likeType.likeNames &&
                                    likeType.likeNames.length > 0 && (
                                        <div className="tooltip">
                                            {likeType.likeNames.map(
                                                (
                                                    name: string,
                                                    index: number
                                                ) => (
                                                    <div key={index}>
                                                        {name}
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    )}
                            </div>
                        )
                    })}
                    {/*<span style={{marginLeft: '3px'}}>{totalLikes}</span>*/}

                    <div
                        className="card-container-like-buttons"
                        onMouseEnter={onEnterReactions}
                        onMouseLeave={onLeaveReactions}
                    >
                        {showReactions[post.id] && (
                            <div className="reaction-tooltip">
                                <img
                                    className="tooltip-item"
                                    onClick={() => onLike(post, 1)}
                                    src="/icons/me_gusta.svg"
                                    alt={translate(t, "news.TR_LIKE_REACTION")}
                                    style={{
                                        width: '25px',
                                        height: '25px',
                                        borderRadius: '8px',
                                        padding: '2px',
                                        backgroundColor:
                                            post.user_like?.likeTypeId === 1
                                                ? 'lightblue'
                                                : 'transparent',
                                    }}
                                />
                                <img
                                    className="tooltip-item"
                                    onClick={() => onLike(post, 2)}
                                    src="/icons/me_encanta.svg"
                                    alt={translate(t, "news.TR_LOVE_REACTION")}
                                    style={{
                                        width: '25px',
                                        height: '25px',
                                        borderRadius: '8px',
                                        padding: '2px',
                                        backgroundColor:
                                            post.user_like?.likeTypeId === 2
                                                ? 'lightblue'
                                                : 'transparent',
                                    }}
                                />
                                <img
                                    className="tooltip-item"
                                    onClick={() => onLike(post, 3)}
                                    src="/icons/me_divierte.svg"
                                    alt={translate(t, "news.TR_FUN_REACTION")}
                                    style={{
                                        width: '25px',
                                        height: '25px',
                                        borderRadius: '8px',
                                        padding: '2px',
                                        backgroundColor:
                                            post.user_like?.likeTypeId === 3
                                                ? 'lightblue'
                                                : 'transparent',
                                    }}
                                />
                            </div>
                        )}
                        <button
                            className="like-button"
                            style={{ marginRight: '10px' }}
                        >
                            {translate(t, 'news.TR_LIKE_LABEL')}
                        </button>
                    </div>

                    <div
                        className="views-section"
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        <i
                            className="fas fa-eye"
                            style={{ marginRight: '5px' }}
                        ></i>
                        <span>{post.views}</span>{' '}
                        {/* Muestra el número de vistas */}
                        {post.view_names && post.view_names.length > 0 ? (
                            <div className="tooltip">
                                {post.view_names.map((name, index) => (
                                    <div key={index}>{name}</div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default NewsCard
